'use client';

import { useState } from 'react';
import { ScoredTechnology } from '@/lib/tech-stack-recommender';
import { UserRequirements } from '@/lib/tech-stack-recommender';

interface ADRGeneratorProps {
  selectedTechnology: ScoredTechnology;
  alternatives: ScoredTechnology[];
  requirements: UserRequirements;
  allRecommendations?: ScoredTechnology[];
  alternativeStacks?: any[];
  risks?: any[];
}

type ExportFormat = 'markdown' | 'html' | 'json' | 'pdf';

export default function TechStackADRGenerator({
  selectedTechnology,
  alternatives,
  requirements,
  allRecommendations = [],
  alternativeStacks = [],
  risks = [],
}: ADRGeneratorProps) {
  const [adrNumber, setAdrNumber] = useState('001');
  const [additionalContext, setAdditionalContext] = useState('');
  const [projectName, setProjectName] = useState('');
  const [exportFormat, setExportFormat] = useState<ExportFormat>('markdown');
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);

  const generateADR = (): string => {
    const date = new Date().toISOString().split('T')[0];
    const tech = selectedTechnology;

    const adr = `# ADR-${adrNumber}: ${tech.category} Selection

## Status
Proposed

## Context

### Organization Requirements
- **Team Size**: ${requirements.teamSize}
- **Industry**: ${requirements.industry}
- **Primary Use Cases**: ${requirements.useCases.join(', ')}

### Technical Requirements
- **Scalability Needs**: ${requirements.scalabilityNeeds}
- **Performance Needs**: ${requirements.performanceNeeds}
- **Team Expertise**: ${requirements.teamExpertise}

### Budget & Resources
- **Budget Level**: ${requirements.budget}
- **Maintenance Capability**: ${requirements.maintenanceCapability}

### Operational Requirements
- **Hosting Preference**: ${requirements.hostingPreference}
- **Compliance**: ${[
  requirements.gdprRequired && 'GDPR',
  requirements.hipaaRequired && 'HIPAA',
  requirements.soc2Required && 'SOC 2',
  requirements.pciRequired && 'PCI DSS'
].filter(Boolean).join(', ') || 'None required'}

### Strategic Considerations
- **Vendor Lock-in Tolerance**: ${requirements.vendorLockInTolerance}
- **Open Source Preference**: ${requirements.openSourcePreference}
- **Innovation vs Stability**: ${requirements.innovationVsStability}

${additionalContext ? `\n### Additional Context\n${additionalContext}\n` : ''}

## Decision

We will use **${tech.name}** for ${tech.category}.

**Official Website**: ${tech.website}
**License**: ${tech.license}
**Pricing**: ${tech.pricing.model}${tech.pricing.startingPrice ? ` (${tech.pricing.startingPrice})` : ''}

## Rationale

### Why ${tech.name}?

${tech.matchReasons.map((reason, i) => `${i + 1}. ${reason}`).join('\n')}

### Scoring

- **Overall Score**: ${tech.score.toFixed(1)}/100 (Rank #${tech.rank})

#### Dimension Breakdown
- **Compliance**: ${Math.round(tech.scoreBreakdown.compliance * 100)}/100
- **Technical**: ${Math.round(tech.scoreBreakdown.technical * 100)}/100
- **Operational**: ${Math.round(tech.scoreBreakdown.operational * 100)}/100
- **Cost**: ${Math.round(tech.scoreBreakdown.cost * 100)}/100
- **Ecosystem**: ${Math.round(tech.scoreBreakdown.ecosystem * 100)}/100
- **Strategic**: ${Math.round(tech.scoreBreakdown.strategic * 100)}/100

### Comparison with Alternatives

${alternatives.slice(0, 3).map(alt => `
#### ${alt.name} (Score: ${alt.score.toFixed(1)}/100)

**Why not chosen**:
${alt.score < tech.score ? `Lower overall score (${(tech.score - alt.score).toFixed(1)} points difference)` : 'Specific requirements better met by our choice'}

**Key differences**:
- License: ${alt.license} vs ${tech.license}
- Scalability: ${alt.technical.scalability} vs ${tech.technical.scalability}
- Learning Curve: ${alt.technical.learningCurve} vs ${tech.technical.learningCurve}
- Vendor Lock-in: ${alt.migration.vendorLockIn} vs ${tech.migration.vendorLockIn}
`).join('\n')}

## Consequences

### Positive

${tech.suitability.strengths.map(s => `- ${s}`).join('\n')}

### Negative / Trade-offs

${tech.suitability.weaknesses.map(w => `- ${w}`).join('\n')}

${tech.concerns.length > 0 ? `\n### Considerations\n\n${tech.concerns.map(c => `- ${c}`).join('\n')}` : ''}

### Risks & Mitigation

${generateRiskMitigation(tech)}

## Implementation Notes

### Getting Started

1. **Setup**: ${getSetupNotes(tech)}
2. **Hosting**: ${tech.operations.hostingOptions.join(' or ')}
3. **Maintenance**: ${tech.operations.maintenanceComplexity} complexity expected
4. **Monitoring**: Consider ${tech.operations.monitoringComplexity} monitoring complexity

### Key Considerations

- **Data Portability**: ${tech.migration.dataPortability}
- **Backup/Recovery**: ${tech.operations.backupRecovery}
- **Community Size**: ${tech.ecosystem.communitySize}
- **Documentation**: ${tech.ecosystem.documentation}

### Migration Path (if needed)

Possible alternatives or migration targets:
${tech.migration.migrationPath.map(path => `- ${path}`).join('\n')}

## References

- **Official Website**: [${tech.website}](${tech.website})
- **Documentation**: ${tech.ecosystem.documentation}
- **Community Size**: ${tech.ecosystem.communitySize}
- **Enterprise Support**: ${tech.ecosystem.enterpriseSupport ? 'Available' : 'Not available'}

${tech.content.comparisonSlug ? `- **Detailed Comparison**: [View on The Art of CTO](/comparisons/${tech.content.comparisonSlug})` : ''}

---

**Decision Made**: ${date}
**Next Review**: ${getReviewDate(date)}

*Generated by The Art of CTO Tech Stack Decision Engine*
`;

    return adr;
  };

  const generateHTML = (): string => {
    const markdown = generateADR();
    // Simple markdown to HTML conversion
    return `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <title>ADR-${adrNumber}: ${selectedTechnology.category} Selection</title>
  <style>
    body {
      font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
      max-width: 900px;
      margin: 40px auto;
      padding: 20px;
      background: #f5f5f5;
      color: #333;
    }
    .container {
      background: white;
      padding: 40px;
      border-radius: 8px;
      box-shadow: 0 2px 8px rgba(0,0,0,0.1);
    }
    h1 { color: #0A1E2E; border-bottom: 3px solid #E89841; padding-bottom: 10px; }
    h2 { color: #0A1E2E; margin-top: 30px; border-bottom: 1px solid #E8D5B7; padding-bottom: 8px; }
    h3 { color: #E89841; }
    code { background: #f4f4f4; padding: 2px 6px; border-radius: 3px; }
    pre { background: #f4f4f4; padding: 15px; border-radius: 5px; overflow-x: auto; }
    ul, ol { line-height: 1.8; }
    .score { background: #E89841; color: white; padding: 4px 12px; border-radius: 4px; font-weight: bold; }
    .footer { margin-top: 40px; padding-top: 20px; border-top: 1px solid #E8D5B7; color: #666; font-size: 14px; }
    @media print {
      body { background: white; }
      .container { box-shadow: none; }
    }
  </style>
</head>
<body>
  <div class="container">
    <pre>${markdown.replace(/</g, '&lt;').replace(/>/g, '&gt;')}</pre>
  </div>
</body>
</html>`;
  };

  const generateJSON = (): string => {
    const tech = selectedTechnology;
    const data = {
      adr: {
        number: adrNumber,
        title: `${tech.category} Selection`,
        status: 'Proposed',
        date: new Date().toISOString().split('T')[0],
      },
      decision: {
        technology: tech.name,
        category: tech.category,
        website: tech.website,
        license: tech.license,
        pricing: tech.pricing,
      },
      context: {
        organization: {
          teamSize: requirements.teamSize,
          industry: requirements.industry,
          useCases: requirements.useCases,
        },
        technical: {
          scalabilityNeeds: requirements.scalabilityNeeds,
          performanceNeeds: requirements.performanceNeeds,
          teamExpertise: requirements.teamExpertise,
        },
        budget: {
          budgetLevel: requirements.budget,
          maintenanceCapability: requirements.maintenanceCapability,
        },
        operational: {
          hostingPreference: requirements.hostingPreference,
          compliance: {
            gdpr: requirements.gdprRequired,
            hipaa: requirements.hipaaRequired,
            soc2: requirements.soc2Required,
            pci: requirements.pciRequired,
          },
        },
        strategic: {
          vendorLockInTolerance: requirements.vendorLockInTolerance,
          openSourcePreference: requirements.openSourcePreference,
          innovationVsStability: requirements.innovationVsStability,
        },
        additionalContext: additionalContext,
      },
      rationale: {
        score: tech.score,
        rank: tech.rank,
        scoreBreakdown: tech.scoreBreakdown,
        matchReasons: tech.matchReasons,
        concerns: tech.concerns,
      },
      alternatives: alternatives.slice(0, 3).map(alt => ({
        name: alt.name,
        score: alt.score,
        license: alt.license,
        scalability: alt.technical.scalability,
        learningCurve: alt.technical.learningCurve,
        vendorLockIn: alt.migration.vendorLockIn,
      })),
      consequences: {
        positive: tech.suitability.strengths,
        negative: tech.suitability.weaknesses,
        considerations: tech.concerns,
      },
      implementation: {
        hostingOptions: tech.operations.hostingOptions,
        maintenanceComplexity: tech.operations.maintenanceComplexity,
        monitoringComplexity: tech.operations.monitoringComplexity,
        dataPortability: tech.migration.dataPortability,
        backupRecovery: tech.operations.backupRecovery,
        migrationPath: tech.migration.migrationPath,
      },
      references: {
        website: tech.website,
        documentation: tech.ecosystem.documentation,
        communitySize: tech.ecosystem.communitySize,
        enterpriseSupport: tech.ecosystem.enterpriseSupport,
      },
    };

    return JSON.stringify(data, null, 2);
  };

  const generatePDF = async () => {
    setIsGeneratingPDF(true);
    try {
      // Dynamic import to avoid SSR issues
      const { pdf } = await import('@react-pdf/renderer');
      const { TechStackPDFDocument } = await import('./pdf/TechStackPDFDocument');

      // Use all recommendations or just the selected one
      const recommendations = allRecommendations.length > 0
        ? allRecommendations.slice(0, 10) // Limit to top 10 to avoid huge files
        : [selectedTechnology];

      const doc = TechStackPDFDocument({
        requirements,
        recommendations,
        alternativeStacks: alternativeStacks.slice(0, 5), // Limit to 5 alternative stacks
        risks,
        projectName: projectName || undefined,
      });

      const blob = await pdf(doc).toBlob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `tech-stack-decision-${projectName ? `${projectName.replace(/\s+/g, '-')}-` : ''}${Date.now()}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('PDF generation error:', error);
      alert('Failed to generate PDF. Please try another export format.');
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  const handleExport = async () => {
    if (exportFormat === 'pdf') {
      await generatePDF();
      return;
    }

    let content: string;
    let filename: string;
    let mimeType: string;

    switch (exportFormat) {
      case 'markdown':
        content = generateADR();
        filename = `ADR-${adrNumber}-${selectedTechnology.id}.md`;
        mimeType = 'text/markdown';
        break;
      case 'html':
        content = generateHTML();
        filename = `ADR-${adrNumber}-${selectedTechnology.id}.html`;
        mimeType = 'text/html';
        break;
      case 'json':
        content = generateJSON();
        filename = `ADR-${adrNumber}-${selectedTechnology.id}.json`;
        mimeType = 'application/json';
        break;
      default:
        return;
    }

    const blob = new Blob([content], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  const copyToClipboard = () => {
    const content = exportFormat === 'json' ? generateJSON() : generateADR();
    navigator.clipboard.writeText(content);
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Configuration */}
      <div className="bg-surface rounded-lg p-6">
        <h3 className="text-xl font-bold text-foreground mb-4">ADR Configuration</h3>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">ADR Number</label>
            <input
              type="text"
              value={adrNumber}
              onChange={(e) => setAdrNumber(e.target.value)}
              className="w-full p-3 bg-background border border-border rounded-lg text-foreground focus:border-accent focus:outline-none"
              placeholder="001"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Project Name (optional)
            </label>
            <input
              type="text"
              value={projectName}
              onChange={(e) => setProjectName(e.target.value)}
              className="w-full p-3 bg-background border border-border rounded-lg text-foreground focus:border-accent focus:outline-none"
              placeholder="My Project"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">Export Format</label>
            <select
              value={exportFormat}
              onChange={(e) => setExportFormat(e.target.value as ExportFormat)}
              className="w-full p-3 bg-background border border-border rounded-lg text-foreground focus:border-accent focus:outline-none"
            >
              <option value="markdown">Markdown (.md)</option>
              <option value="html">HTML (.html)</option>
              <option value="json">JSON (.json)</option>
              <option value="pdf">PDF (.pdf)</option>
            </select>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-foreground mb-2">
            Additional Context (optional)
          </label>
          <textarea
            value={additionalContext}
            onChange={(e) => setAdditionalContext(e.target.value)}
            rows={4}
            className="w-full p-3 bg-background border border-border rounded-lg text-foreground focus:border-accent focus:outline-none resize-none"
            placeholder="Add any specific context about this decision..."
          />
        </div>
      </div>

      {/* Preview */}
      <div className="bg-surface rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xl font-bold text-foreground">Preview</h3>
          <div className="space-x-2">
            {exportFormat !== 'pdf' && (
              <button
                onClick={copyToClipboard}
                className="px-4 py-2 bg-background text-foreground rounded-lg text-sm hover:bg-background/70 transition-colors"
              >
                Copy to Clipboard
              </button>
            )}
            <button
              onClick={handleExport}
              disabled={isGeneratingPDF}
              className="px-6 py-2 bg-accent text-accent-foreground rounded-lg text-sm font-semibold hover:bg-accent-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isGeneratingPDF ? 'Generating PDF...' : `Export ${exportFormat.toUpperCase()}`}
            </button>
          </div>
        </div>

        {exportFormat === 'pdf' ? (
          <div className="bg-background rounded-lg p-6">
            <div className="text-center py-12">
              <div className="text-6xl mb-4">📄</div>
              <h4 className="text-lg font-semibold text-foreground mb-2">PDF Report</h4>
              <p className="text-sm text-foreground-secondary mb-6">
                Click &quot;Export PDF&quot; above to generate a professional PDF report including:
              </p>
              <ul className="text-left max-w-md mx-auto space-y-2 text-sm text-foreground/90">
                <li className="flex items-start">
                  <span className="text-accent mr-2">✓</span>
                  <span>Cover page with project details</span>
                </li>
                <li className="flex items-start">
                  <span className="text-accent mr-2">✓</span>
                  <span>Executive summary of requirements</span>
                </li>
                <li className="flex items-start">
                  <span className="text-accent mr-2">✓</span>
                  <span>Detailed technology recommendations with scores</span>
                </li>
                <li className="flex items-start">
                  <span className="text-accent mr-2">✓</span>
                  <span>Alternative stack comparisons</span>
                </li>
                <li className="flex items-start">
                  <span className="text-accent mr-2">✓</span>
                  <span>Risk assessment and mitigation strategies</span>
                </li>
              </ul>
            </div>
          </div>
        ) : (
          <div className="bg-background rounded-lg p-6 overflow-x-auto">
            <pre className="text-sm text-foreground/90 whitespace-pre-wrap font-mono">
              {exportFormat === 'json' ? generateJSON() : generateADR()}
            </pre>
          </div>
        )}
      </div>
    </div>
  );
}

function getSetupNotes(tech: ScoredTechnology): string {
  const notes = {
    'open-source': 'Download and install from official repository',
    'commercial': 'Sign up for account and configure licensing',
    'freemium': 'Start with free tier, upgrade as needed',
    'enterprise-only': 'Contact sales team for enterprise setup',
  };
  return notes[tech.license] || 'Follow official installation guide';
}

function generateRiskMitigation(tech: ScoredTechnology): string {
  const risks: string[] = [];

  if (tech.migration.vendorLockIn === 'high') {
    risks.push('**Risk**: High vendor lock-in\n  **Mitigation**: Document migration paths, maintain data portability standards');
  }

  if (tech.technical.learningCurve === 'steep' || tech.technical.learningCurve === 'very-steep') {
    risks.push('**Risk**: Steep learning curve\n  **Mitigation**: Invest in team training, hire experienced practitioners');
  }

  if (tech.operations.maintenanceComplexity === 'high') {
    risks.push('**Risk**: High maintenance overhead\n  **Mitigation**: Consider managed service options, allocate dedicated ops resources');
  }

  if (tech.technical.maturity === 'early' || tech.technical.maturity === 'experimental') {
    risks.push('**Risk**: Technology still maturing\n  **Mitigation**: Plan for breaking changes, maintain fallback options');
  }

  return risks.length > 0 ? risks.join('\n\n') : 'No significant risks identified for this technology choice.';
}

function getReviewDate(date: string): string {
  const reviewDate = new Date(date);
  reviewDate.setMonth(reviewDate.getMonth() + 6);
  return reviewDate.toISOString().split('T')[0];
}
